import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Images, MapPin, Calendar, Eye } from "lucide-react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

// Helper to resolve image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

function GalleryCard({ gallery }) {
  const coverImage = getImageUrl(gallery.cover_image_url) || 
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Link
        to={`/galerias/${gallery.slug}`}
        className="block bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500"
        data-testid={`gallery-card-${gallery.slug}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={coverImage}
            alt={gallery.title}
            className="w-full h-full object-cover img-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Image count badge */}
          <div className="absolute top-4 right-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full flex items-center gap-1.5">
            <Images className="h-4 w-4 text-white" />
            <span className="text-white text-sm font-medium">{gallery.image_count || 0}</span>
          </div>
          
          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-heading text-xl font-bold text-white mb-1 line-clamp-2">
              {gallery.title}
            </h3>
            {gallery.location && (
              <p className="text-white/80 text-sm flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {gallery.location}
              </p>
            )}
          </div>
        </div>
        
        <div className="p-4">
          {gallery.description && (
            <p className="text-brand-textSecondary text-sm line-clamp-2 mb-3">
              {gallery.description}
            </p>
          )}
          <div className="flex items-center justify-between text-xs text-brand-textMuted">
            {gallery.date_taken && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {gallery.date_taken}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {gallery.view_count || 0} vistas
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function GaleriasPage() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await axios.get(`${API}/public/galleries?limit=50`);
        setGalleries(response.data.items);
      } catch (error) {
        console.error("Error fetching galleries:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalleries();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="galerias"
        title="Galerías de Fotos"
        subtitle="Explora nuestra colección de fotografías que capturan la belleza y esencia de Choluteca"
        badge="Colección Fotográfica"
        badgeIcon={Images}
      />

      {/* Galleries Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : galleries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleries.map((gallery, index) => (
                <motion.div
                  key={gallery.gallery_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <GalleryCard gallery={gallery} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Images className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No hay galerías aún</h3>
              <p className="text-gray-500">Pronto compartiremos hermosas colecciones de fotos</p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
